import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  DollarSign, 
  Calendar, 
  Users, 
  TrendingUp,
  Search,
  Filter,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  ChevronRight,
  User,
  Building2,
  Send,
  FileText,
  Download,
  RefreshCw,
  Star,
  Banknote
} from 'lucide-react';

const InterestDistribution = () => {
  const [activeTab, setActiveTab] = useState('upcoming');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedDistribution, setSelectedDistribution] = useState(null);
  const [showDistributionModal, setShowDistributionModal] = useState(false);

  // Mock data for upcoming distributions
  const [upcomingDistributions] = useState([
    {
      id: 'DIST-001',
      bondName: 'Green Infrastructure Bond 2024-A',
      bondType: 'Fixed Rate Note',
      paymentDate: '2024-02-15T00:00:00Z',
      paymentType: 'Coupon Payment',
      couponRate: '4.5%',
      totalAmount: 'MYR 45,000',
      status: 'ready_for_distribution',
      totalInvestors: 3,
      fundingConfirmed: true,
      fundingDate: '2024-02-10T14:30:00Z',
      eligibleInvestors: [
        {
          investorId: 'INV-001',
          investorName: 'Ahmad bin Rahman',
          investorType: 'Individual',
          walletAddress: '0x742dE5b2D72E5a85e9c1C5f7A2B8D34F2E1A9C6B',
          tokenHolding: 1000,
          paymentAmount: 'MYR 4,500',
          percentage: 10
        },
        {
          investorId: 'INV-002',
          investorName: 'Tech Solutions Sdn Bhd',
          investorType: 'Corporate',
          walletAddress: '0x852fF3c4D32A6b85e9c2D8g8B3C9E27F3D4A8B5C',
          tokenHolding: 5000,
          paymentAmount: 'MYR 22,500',
          percentage: 50
        },
        {
          investorId: 'INV-003',
          investorName: 'Global Investment Ltd',
          investorType: 'Corporate',
          walletAddress: '0x963eE4d5E43B7c96f0c3Eh9C4D0F38E4B7A9D2C5',
          tokenHolding: 3000,
          paymentAmount: 'MYR 13,500',
          percentage: 30
        }
      ]
    },
    {
      id: 'DIST-002',
      bondName: 'Tech Development Sukuk',
      bondType: 'Sukuk',
      paymentDate: '2024-02-20T00:00:00Z',
      paymentType: 'Profit Payment',
      profitRate: '5.2%',
      totalAmount: 'MYR 26,000',
      status: 'pending_funding',
      totalInvestors: 2,
      fundingConfirmed: false,
      eligibleInvestors: [
        {
          investorId: 'INV-004',
          investorName: 'Jennifer Wong Su Ling',
          investorType: 'Individual',
          walletAddress: '0xabc123def456ghi789jkl012mno345pqr678stu901',
          tokenHolding: 2000,
          paymentAmount: 'MYR 10,400',
          percentage: 40
        },
        {
          investorId: 'INV-005',
          investorName: 'Innovation Holdings Bhd',
          investorType: 'Corporate',
          walletAddress: '0xdef456ghi789jkl012mno345pqr678stu901vwx234',
          tokenHolding: 3000,
          paymentAmount: 'MYR 15,600',
          percentage: 60
        }
      ]
    },
    {
      id: 'DIST-003',
      bondName: 'Infrastructure Bond 2022-A',
      bondType: 'Fixed Rate Note',
      paymentDate: '2024-03-01T00:00:00Z',
      paymentType: 'Coupon Payment',
      couponRate: '4.2%',
      totalAmount: 'MYR 63,000',
      status: 'scheduled',
      totalInvestors: 4,
      fundingConfirmed: false,
      eligibleInvestors: [
        {
          investorId: 'INV-006',
          investorName: 'Pension Fund Malaysia',
          investorType: 'Corporate',
          walletAddress: '0x123456789abcdef123456789abcdef123456789a',
          tokenHolding: 7500,
          paymentAmount: 'MYR 31,500',
          percentage: 50
        },
        {
          investorId: 'INV-007',
          investorName: 'Malaysian Investment Fund',
          investorType: 'Corporate',
          walletAddress: '0x987654321fedcba987654321fedcba987654321f',
          tokenHolding: 4500,
          paymentAmount: 'MYR 18,900',
          percentage: 30
        },
        {
          investorId: 'INV-008',
          investorName: 'Retirement Savings Corp',
          investorType: 'Corporate',
          walletAddress: '0xfedcba987654321fedcba987654321fedcba9876',
          tokenHolding: 3000,
          paymentAmount: 'MYR 12,600',
          percentage: 20
        }
      ]
    }
  ]);

  // Mock data for distribution history
  const [distributionHistory] = useState([
    {
      id: 'DIST-HIST-001',
      bondName: 'Corporate Expansion Bond',
      bondType: 'Fixed Rate Note',
      paymentDate: '2024-01-15T00:00:00Z',
      paymentType: 'Coupon Payment',
      couponRate: '3.8%',
      totalAmount: 'MYR 38,000',
      status: 'distributed',
      totalInvestors: 5,
      distributionDate: '2024-01-15T11:30:00Z',
      transactionHash: '0xabc123def456ghi789jkl012mno345pqr678stu901vwx234yz',
      processedBy: 'custodian.admin@bank.com',
      proofDocument: 'distribution_proof_DIST-HIST-001.pdf'
    },
    {
      id: 'DIST-HIST-002',
      bondName: 'Renewable Energy Sukuk Series B',
      bondType: 'Sukuk',
      paymentDate: '2024-01-10T00:00:00Z',
      paymentType: 'Profit Payment',
      profitRate: '4.8%',
      totalAmount: 'MYR 72,000',
      status: 'distributed',
      totalInvestors: 3,
      distributionDate: '2024-01-10T14:45:00Z',
      transactionHash: '0xdef456ghi789jkl012mno345pqr678stu901vwx234yz567',
      processedBy: 'custodian.admin@bank.com',
      proofDocument: 'distribution_proof_DIST-HIST-002.pdf'
    },
    {
      id: 'DIST-HIST-003',
      bondName: 'Government Infrastructure Bond 2023',
      bondType: 'Fixed Rate Note',
      paymentDate: '2024-01-05T00:00:00Z',
      paymentType: 'Coupon Payment',
      couponRate: '4.0%',
      totalAmount: 'MYR 120,000',
      status: 'failed',
      totalInvestors: 6,
      failureReason: 'Insufficient funding from facility agent',
      failureDate: '2024-01-05T09:15:00Z'
    }
  ]);

  const tabs = [
    {
      id: 'upcoming',
      name: 'Upcoming Distributions',
      icon: Calendar,
      description: 'Scheduled interest and profit distributions'
    },
    {
      id: 'history',
      name: 'Distribution History',
      icon: FileText,
      description: 'Past distribution records and status'
    }
  ];

  const filteredUpcoming = upcomingDistributions.filter(dist => {
    const matchesSearch = dist.bondName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         dist.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         dist.paymentType.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || dist.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const filteredHistory = distributionHistory.filter(dist => {
    const matchesSearch = dist.bondName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         dist.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         dist.paymentType.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || dist.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getUpcomingStatusBadge = (status) => {
    const statusClasses = {
      scheduled: 'bg-gray-100 text-gray-800',
      pending_funding: 'bg-yellow-100 text-yellow-800',
      ready_for_distribution: 'bg-blue-100 text-blue-800',
      processing: 'bg-purple-100 text-purple-800'
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-800';
  };

  const getHistoryStatusBadge = (status) => {
    const statusClasses = {
      distributed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'scheduled':
      case 'pending_funding':
        return <Clock className="h-4 w-4" />;
      case 'ready_for_distribution':
      case 'processing':
        return <RefreshCw className="h-4 w-4" />;
      case 'distributed':
        return <CheckCircle className="h-4 w-4" />;
      case 'failed':
      case 'cancelled':
        return <XCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const handleViewDistribution = (distribution) => {
    setSelectedDistribution(distribution);
    setShowDistributionModal(true);
  };

  const handleInitiateDistribution = () => {
    console.log('Initiating distribution for:', selectedDistribution.id);
    setShowDistributionModal(false);
    // In real implementation, this would call API to initiate distribution
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isPaymentDue = (paymentDate) => {
    const today = new Date();
    const payment = new Date(paymentDate);
    return payment <= today;
  };

  const getDaysUntilPayment = (paymentDate) => {
    const today = new Date();
    const payment = new Date(paymentDate);
    const diffTime = payment - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2">
              <Link to="/custodian" className="text-gray-400 hover:text-gray-600">
                Custodian Dashboard
              </Link>
              <ChevronRight className="h-4 w-4 text-gray-400" />
              <span className="text-gray-900 font-medium">Interest/Profit Distribution</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Interest/Profit Distribution</h1>
          <p className="mt-2 text-gray-600">
            Manage periodic interest payments for Fixed Rate Notes and profit distributions for Sukuk.
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Upcoming Distributions Tab */}
        {activeTab === 'upcoming' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white shadow rounded-lg p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Search Distributions
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by bond name, payment type..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="all">All Status</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="pending_funding">Pending Funding</option>
                    <option value="ready_for_distribution">Ready for Distribution</option>
                    <option value="processing">Processing</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setFilterStatus('all');
                    }}
                    className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>

            {/* Distributions Calendar/List */}
            <div className="bg-white shadow rounded-lg overflow-hidden overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">
                      Bond & Payment Details
                    </th>
                    <th className="table-header-cell">
                      Payment Date
                    </th>
                    <th className="table-header-cell">
                      Rate & Amount
                    </th>
                    <th className="table-header-cell">
                      Investors
                    </th>
                    <th className="table-header-cell">
                      Status
                    </th>
                    <th className="table-header-cell">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUpcoming.map((distribution) => {
                    const daysUntil = getDaysUntilPayment(distribution.paymentDate);
                    const isDue = isPaymentDue(distribution.paymentDate);
                    
                    return (
                      <tr key={distribution.id} className={`hover:bg-gray-50 ${isDue ? 'bg-yellow-50' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {distribution.bondName}
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                distribution.bondType === 'Sukuk' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-blue-100 text-blue-800'
                              }`}>
                                {distribution.bondType}
                              </span>
                              <span className="text-sm text-gray-500">
                                {distribution.paymentType}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm">
                            <div className="font-medium text-gray-900">
                              {formatDate(distribution.paymentDate)}
                            </div>
                            <div className={`text-xs ${isDue ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                              {isDue ? 'Payment Due' : `${daysUntil} days remaining`}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm">
                            <div className="font-medium text-gray-900">
                              {distribution.couponRate || distribution.profitRate}
                            </div>
                            <div className="text-gray-500 font-semibold">
                              {distribution.totalAmount}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {distribution.totalInvestors} investors
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col space-y-1">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getUpcomingStatusBadge(distribution.status)}`}>
                              {getStatusIcon(distribution.status)}
                              <span className="ml-1 capitalize">{distribution.status.replace('_', ' ')}</span>
                            </span>
                            {distribution.fundingConfirmed && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Funded
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleViewDistribution(distribution)}
                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              View Details
                            </button>
                            {distribution.status === 'ready_for_distribution' && (
                              <button
                                onClick={() => handleViewDistribution(distribution)}
                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                              >
                                <Send className="h-3 w-3 mr-1" />
                                Initiate
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              
              {filteredUpcoming.length === 0 && (
                <div className="text-center py-12">
                  <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No upcoming distributions found</h3>
                  <p className="text-gray-600">
                    {searchTerm || filterStatus !== 'all'
                      ? 'Try adjusting your search or filter criteria.'
                      : 'No scheduled distributions at this time.'
                    }
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Distribution History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white shadow rounded-lg p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Search History
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by bond name, payment type..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="all">All Status</option>
                    <option value="distributed">Distributed</option>
                    <option value="failed">Failed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setFilterStatus('all');
                    }}
                    className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>

            {/* History Table */}
            <div className="bg-white shadow rounded-lg overflow-hidden overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">
                      Bond & Payment Details
                    </th>
                    <th className="table-header-cell">
                      Payment Date
                    </th>
                    <th className="table-header-cell">
                      Rate & Amount
                    </th>
                    <th className="table-header-cell">
                      Investors
                    </th>
                    <th className="table-header-cell">
                      Status
                    </th>
                    <th className="table-header-cell">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredHistory.map((distribution) => (
                    <tr key={distribution.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {distribution.bondName}
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              distribution.bondType === 'Sukuk' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {distribution.bondType}
                            </span>
                            <span className="text-sm text-gray-500">
                              {distribution.paymentType}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">
                            {formatDate(distribution.paymentDate)}
                          </div>
                          {distribution.distributionDate && (
                            <div className="text-xs text-gray-500">
                              Processed: {formatDate(distribution.distributionDate)}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">
                            {distribution.couponRate || distribution.profitRate}
                          </div>
                          <div className="text-gray-500 font-semibold">
                            {distribution.totalAmount}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {distribution.totalInvestors} investors
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getHistoryStatusBadge(distribution.status)}`}>
                          {getStatusIcon(distribution.status)}
                          <span className="ml-1 capitalize">{distribution.status}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                            <Eye className="h-3 w-3 mr-1" />
                            View Details
                          </button>
                          {distribution.proofDocument && (
                            <button className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
                              <Download className="h-3 w-3 mr-1" />
                              Proof
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {filteredHistory.length === 0 && (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No distribution history found</h3>
                  <p className="text-gray-600">
                    {searchTerm || filterStatus !== 'all'
                      ? 'Try adjusting your search or filter criteria.'
                      : 'No past distributions to display.'
                    }
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Distribution Detail Modal */}
      {showDistributionModal && selectedDistribution && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">Distribution Details</h3>
                <button
                  onClick={() => setShowDistributionModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  ×
                </button>
              </div>

              <div className="space-y-6">
                {/* Bond Information */}
                <div className="border rounded-lg p-4 bg-gray-50">
                  <h4 className="font-medium text-gray-900 mb-3">Bond & Payment Information</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Bond Name:</span>
                      <span className="ml-2 font-medium">{selectedDistribution.bondName}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Payment Type:</span>
                      <span className="ml-2 font-medium">{selectedDistribution.paymentType}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Rate:</span>
                      <span className="ml-2 font-medium">
                        {selectedDistribution.couponRate || selectedDistribution.profitRate}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Total Amount:</span>
                      <span className="ml-2 font-medium">{selectedDistribution.totalAmount}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Payment Date:</span>
                      <span className="ml-2 font-medium">{formatDate(selectedDistribution.paymentDate)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Total Investors:</span>
                      <span className="ml-2 font-medium">{selectedDistribution.totalInvestors}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Funding Status:</span>
                      <span className={`ml-2 font-medium ${selectedDistribution.fundingConfirmed ? 'text-green-600' : 'text-yellow-600'}`}>
                        {selectedDistribution.fundingConfirmed ? 'Confirmed' : 'Pending'}
                      </span>
                    </div>
                    {selectedDistribution.fundingDate && (
                      <div>
                        <span className="text-gray-500">Funding Date:</span>
                        <span className="ml-2 font-medium">{formatDate(selectedDistribution.fundingDate)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Investor Breakdown */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Investor Payment Breakdown</h4>
                  <div className="overflow-x-auto">
                    <table className="table">
                      <thead className="table-header">
                        <tr>
                          <th className="table-header-cell">
                            Investor
                          </th>
                          <th className="table-header-cell">
                            Wallet Address
                          </th>
                          <th className="table-header-cell">
                            Token Holding
                          </th>
                          <th className="table-header-cell">
                            Payment Amount
                          </th>
                          <th className="table-header-cell">
                            Percentage
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedDistribution.eligibleInvestors.map((investor, index) => (
                          <tr key={index}>
                            <td className="px-4 py-2 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-8 w-8">
                                  <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                                    {investor.investorType === 'Individual' ? (
                                      <User className="h-4 w-4 text-gray-600" />
                                    ) : (
                                      <Building2 className="h-4 w-4 text-gray-600" />
                                    )}
                                  </div>
                                </div>
                                <div className="ml-3">
                                  <div className="text-sm font-medium text-gray-900">
                                    {investor.investorName}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {investor.investorType}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap">
                              <span className="font-mono text-xs text-gray-600">
                                {investor.walletAddress.substring(0, 10)}...{investor.walletAddress.substring(investor.walletAddress.length - 8)}
                              </span>
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                              {investor.tokenHolding.toLocaleString()} tokens
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                              {investor.paymentAmount}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                              {investor.percentage}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowDistributionModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Close
                </button>
                {selectedDistribution.status === 'ready_for_distribution' && (
                  <button
                    onClick={handleInitiateDistribution}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    Initiate Distribution
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterestDistribution; 